import React, { useState } from 'react';
import { InterPoint } from '@saviynt/common';
import {
  ApplicationIcon,
  Avatar,
  Badge,
  Box,
  Chip,
  Icon,
  ListRow,
  ListRowGroup,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';
import { format } from 'date-fns';

import { CF_NEU_BLD, CF_NEU_STL, CF_SEC_MED } from '../../../../../constants';
import { useInvertedLocalize } from '../../../../../hooks';
import {
  type ImpactSummaryTypes,
  type RemediateAccountItem,
  type RemediateEntitlementItem,
  type RemediateViolationItem,
} from '../../../../../types';

import './SodRemediateImpactSummary.css';

interface SodRemediateImpactSummaryProps {
  selectedFunction: ImpactSummaryTypes;
  isReviewingImpactSummary: boolean;
}

/**
 * TODO:
 * - Write description
 * - Need to use the inline application groups we made
 */
function SodRemediateImpactSummary({
  selectedFunction,
  isReviewingImpactSummary,
}: SodRemediateImpactSummaryProps) {
  const [listViewToggleSelection, setListViewToggleSelection] = useState(1);
  const l = useInvertedLocalize();

  const numberOfEntitlements = selectedFunction.entitlements.length;
  const numberOfAccounts = selectedFunction.accounts.length;
  const numberOfViolations = selectedFunction.violations.length;

  const getBadge = (value: number) => (
    <Badge
      kind='number'
      size='medium'
      badgeStyle='outlined'
      number={value}
      color='info'
    />
  );

  const getEntitlementContent = (item: RemediateEntitlementItem) => (
    <Box className='SodRemediateImpactSummary-centerContentEntitlement'>
      <Box className='SodRemediateImpactSummary-centerTop'>
        <Typography kind='body3' color={CF_SEC_MED}>
          {item.title}
        </Typography>
        <InterPoint />
        <Typography kind='body3' color={CF_SEC_MED}>
          {item.title}
        </Typography>
      </Box>
      <Box className='SodRemediateImpactSummary-centerBottom'>
        <Typography kind='h3' color={CF_NEU_BLD}>
          {item.title}
        </Typography>
        <Typography kind='body3' color={CF_SEC_MED}>
          ({item.title})
        </Typography>
      </Box>
    </Box>
  );

  const getAccountContent = (item: RemediateAccountItem) => (
    <Box className='SodRemediateImpactSummary-centerContentAccount'>
      <Box className='SodRemediateImpactSummary-centerTop'>
        <Typography kind='h3' color={CF_NEU_BLD}>
          {item.name}
        </Typography>
      </Box>
      <Box className='SodRemediateImpactSummary-centerBottom'>
        <Box className='SodRemediateImpactSummary-subGroup'>
          <ApplicationIcon logo='AWS' size='xxSmall' />
          <Typography kind='body2' color={CF_NEU_BLD}>
            {item.application[0]}
          </Typography>
        </Box>
        <InterPoint />
        <Box className='SodRemediateImpactSummary-subGroup'>
          <Avatar size='xxSmall'>
            {item.displayName
              .split(' ')
              .filter(Boolean)
              .map((namePart, i, arr) =>
                i === 0 || i === arr.length - 1 ? namePart[0] : ''
              )
              .join('')
              .toUpperCase()}
          </Avatar>
          <Typography kind='body2' color={CF_NEU_BLD}>
            {item.name}
          </Typography>
        </Box>
        <InterPoint />
        <Box className='SodRemediateImpactSummary-subGroup'>
          <Typography kind='body2' color={CF_NEU_STL}>
            Last Login:
          </Typography>
          <Typography kind='body2' color={CF_NEU_BLD}>
            {format(item.lastLogInDate, 'MM/dd/yyyy')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const getViolationContent = (item: RemediateViolationItem) => (
    <Box className='SodRemediateImpactSummary-centerContentAccount'>
      <Box className='SodRemediateImpactSummary-centerTop'>
        <Typography kind='h3' color={CF_NEU_BLD}>
          {item.title}
        </Typography>
      </Box>
      <Box className='SodRemediateImpactSummary-centerBottom'>
        <Chip
          label={item.trustScore}
          kind='subtleFilled'
          color='critical'
          size='xxSmall'
          isInteractionDisabled
        />
        <InterPoint />
        <Chip
          label={item.liklyHood}
          kind='subtleFilled'
          color='warning'
          size='xxSmall'
          isInteractionDisabled
        />
        <InterPoint />
        <Box className='SodRemediateImpactSummary-subGroup'>
          <ApplicationIcon logo='AWS' size='xxSmall' />
          <Typography kind='body2' color={CF_NEU_BLD}>
            {item.application[0]}
          </Typography>
        </Box>
        <InterPoint />
        <Box className='SodRemediateImpactSummary-subGroup'>
          <Avatar size='xxSmall'>
            {item.displayName
              .split(' ')
              .filter(Boolean)
              .map((namePart, i, arr) =>
                i === 0 || i === arr.length - 1 ? namePart[0] : ''
              )
              .join('')
              .toUpperCase()}
          </Avatar>
          <Typography kind='body2' color={CF_NEU_BLD}>
            {item.displayName}
          </Typography>
        </Box>
        <InterPoint />
        <Box className='SodRemediateImpactSummary-subGroup'>
          <Typography kind='body2' color={CF_NEU_STL}>
            Account:
          </Typography>
          <Typography kind='body2' color={CF_NEU_BLD}>
            {item.account}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box tabIndex={-1} className='SodRemediateImpactSummary'>
      <Box className='SodRemediateImpactSummary-titleWrapper'>
        {/* Title */}
        <Typography kind='body2' color={CF_SEC_MED}>
          {l('IMPACT SUMMARY')}
        </Typography>
        {/* Subtitle */}
        <Box className='SodRemediateImpactSummary-subtitleWrapper'>
          <Typography kind='h2' color={CF_NEU_BLD}>
            {l('Revoke')}
          </Typography>
          <Typography kind='h2' color={CF_SEC_MED}>
            {`${numberOfEntitlements} ${l('Entitlements')}`}
          </Typography>
          <Typography kind='h2' color={CF_NEU_BLD}>
            {l('from')}
          </Typography>
          <Typography kind='h2' color={CF_SEC_MED}>
            {`${numberOfAccounts} ${l('Accounts')}`}
          </Typography>
          <Typography kind='h2' color={CF_NEU_BLD}>
            {l('will remediate')}
          </Typography>
          <Typography kind='h2' color={CF_SEC_MED}>
            {`${numberOfViolations} ${l('Violations')}`}
          </Typography>
        </Box>
      </Box>
      {/* List Toggle */}
      <ToggleButtonGroup
        isDisabled={!isReviewingImpactSummary}
        selection={listViewToggleSelection}
        setSelection={setListViewToggleSelection}
        className='SodRemediateImpactSummary-toggleButtonGroup'>
        <ToggleButton
          label='Entitlement'
          icon={<Icon kind='Entitlement' />}
          BadgeComp={getBadge(numberOfEntitlements)}
        />
        <ToggleButton
          label='Account'
          icon={<Icon kind='Account' />}
          BadgeComp={getBadge(numberOfAccounts)}
        />
        <ToggleButton
          label='Violation'
          icon={<Icon kind='SOD' />}
          BadgeComp={getBadge(numberOfViolations)}
        />
      </ToggleButtonGroup>
      {/* Lists being displayed */}
      {/* Entitlements */}
      {listViewToggleSelection === 1 && (
        <ListRowGroup className='SodRemediateImpactSummary-listRowGroup'>
          {selectedFunction.entitlements.map((item) => (
            <ListRow
              key={item.id}
              centerContent={getEntitlementContent(item)}
              leadingComp={<Icon kind='Entitlement' />}
              isCheckboxDisabled
            />
          ))}
        </ListRowGroup>
      )}
      {/* Accounts */}
      {listViewToggleSelection === 2 && (
        <ListRowGroup className='SodRemediateImpactSummary-listRowGroup'>
          {selectedFunction.accounts.map((item) => (
            <ListRow
              key={item.id}
              centerContent={getAccountContent(item)}
              leadingComp={<Icon kind='Account' />}
              isCheckboxDisabled
            />
          ))}
        </ListRowGroup>
      )}
      {/* Violations */}
      {listViewToggleSelection === 3 && (
        <ListRowGroup className='SodRemediateImpactSummary-listRowGroup'>
          {selectedFunction.violations.map((item) => (
            <ListRow
              key={item.id}
              centerContent={getViolationContent(item)}
              leadingComp={<Icon kind='SOD' />}
              isCheckboxDisabled
            />
          ))}
        </ListRowGroup>
      )}
    </Box>
  );
}

export default SodRemediateImpactSummary;
